import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2025-12-01 13:33:59 +0100 (Mon, 01 Dec 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 158800 $
//  Purpose   : react components for debugging
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Icon, Input, Label } from 'semantic-ui-react';
import now from 'performance-now';
// ---Framework imports
import { get, strip, size, keys, dict, padEnd } from 'ldash';
import { uix, Actions, Sel } from 'uix/registries';
import log from 'uix/logging';
import { ResizableDialog, XMenu } from 'uix/components';
import { NodeSwitchItem } from 'uix/nodes/components';
import { useSelector, useActions, usePersistent, useRef, useMemo, useApi, useState, useEffect, useResettableState } from 'uix/hooks';
// ---Local imports
import { applyChannelFilter, channelMatcher, aggregateLines, MULTILINE_PREFIX, XCHAN } from './utils';
let BLUE = 'rgba(0, 55, 218, 1)';
let BLUE_LIGHT = 'rgba(59, 120, 255, 1)';
let RED = 'rgba(197, 15, 31, 1)';
let RED_LIGHT = 'rgba(231, 72, 86, 1)';
let YELLOW = 'rgba(193, 156, 0, 1)';
let YELLOW_LIGHT = 'rgba(249, 241, 165, 1)';
let MAGENTA = 'rgba(136, 23, 152, 1)';
let MAGENTA_LIGHT = 'rgba(180, 0, 58, 1)';
let CYAN = 'rgba(58, 150, 221, 1)';
let CYAN_LIGHT = 'rgba(97, 214, 214, 1)';
let GREEN = 'rgba(19, 161, 14, 1)';
let GREEN_LIGHT = 'rgba(22, 198, 12, 1)';
let BLACK = 'rgba(12, 12, 12, 1)';
let BLACK_LIGHT = 'rgba(118, 118, 118, 1)';
let GREY = 'rgba(204, 204, 204, 1)';
let GREY_LIGHT = 'rgba(242, 242, 242, 1)';
const LEVEL_COLORS = {
    DEBUG: BLUE_LIGHT,
    INFO: GREY_LIGHT,
    INTEREST: GREEN_LIGHT,
    EVENT: CYAN_LIGHT,
    WARNING: YELLOW_LIGHT,
    ERROR: RED_LIGHT
};
const LEVELS = keys(LEVEL_COLORS);
const ChannelDropdown = ({ value, node })=>{
    let apiurl = useSelector(Sel.apiurl);
    let { data, error } = useApi('getLogChannels', {}, {
        cached: true
    });
    let setLogStream = useActions(Actions.setLogStream);
    let options = useMemo(()=>{
        let options = [];
        if (data) {
            options = data.channels.map((c)=>({
                    value: c,
                    text: c
                }));
        } else {
            options.push({
                key: value,
                value,
                text: value
            });
        }
        return options;
    }, [
        data
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(XMenu.Dropdown, {
                placeholder: "Select Channel",
                search: true,
                selection: true,
                value: value,
                options: options,
                onValueChange: (channel)=>setLogStream({
                        channel,
                        node
                    }),
                floating: true,
                minWidth: "100"
            }),
            /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: "external alternate",
                title: "Serve raw log",
                onClick: ()=>window.open(apiurl.href(`/log/serveLog?name=${value}&node=${node}`), 'log_out')
            })
        ]
    });
};
const DEFAULT_ACTIVE = dict(LEVELS.map((l)=>[
        l,
        true
    ]));
const useActiveLevels = ()=>usePersistent('activeLevels', DEFAULT_ACTIVE);
const TRANS = {
    WARNING: 'WARN',
    INTEREST: 'INT',
    ERROR: 'ERR',
    EVENT: 'EVT',
    DEBUG: 'DBG'
};
const LevelSelectors = ({ levelCounts })=>{
    let [active, setActive] = useActiveLevels();
    let toggle = (l)=>setActive({
            ...active,
            [l]: !active[l]
        });
    let content = (l)=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Label, {
                    floating: true,
                    size: "tiny",
                    style: {
                        padding: '0.1em 0.5em',
                        top: '-0.3em'
                    },
                    children: levelCounts[l]
                }),
                /*#__PURE__*/ _jsxs("span", {
                    style: {
                        padding: '3px',
                        fontSize: '0.85rem'
                    },
                    title: `loglevel: ${l}`,
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            style: {
                                backgroundColor: LEVEL_COLORS[l],
                                outline: '1px solid #BBB'
                            },
                            children: "  "
                        }),
                        ' ',
                        TRANS[l] || l
                    ]
                })
            ]
        });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            LEVELS.map((l)=>/*#__PURE__*/ _jsx(XMenu.Item, {
                    fitted: true,
                    active: active[l],
                    onClick: ()=>toggle(l),
                    children: content(l)
                }, l)),
            /*#__PURE__*/ _jsx(XMenu.Item, {
                content: "All",
                onClick: ()=>setActive(DEFAULT_ACTIVE)
            })
        ]
    });
};
const ChannelInfoItem = ({ channels })=>{
    channels = channels.filter((c)=>c != XCHAN);
    return /*#__PURE__*/ _jsx(XMenu.Item, {
        icon: "info",
        content: `${channels.length}`,
        title: channels.join(',')
    });
};
const useAggregatedLoglines = ()=>{
    let log_lines = useSelector((root)=>root.meta.logstream.lines);
    return useMemo(()=>aggregateLines(log_lines || []), [
        log_lines
    ]);
};
export const LogStream = ({ as = 'dialog', channels, limit = false })=>{
    let start_ts = now();
    let divRef = useRef();
    let [recordOn, setRecordOn] = useState(true);
    let [toggleLogStream, setLogStreamMode, setLogStreamData, setLogStream] = useActions([
        'toggleLogStream',
        'setLogStreamMode',
        'setLogStreamData',
        'setLogStream'
    ]);
    let logstream = useSelector((root)=>root.meta.logstream);
    let node = logstream.node || 'host';
    let log_lines = useAggregatedLoglines();
    let isDisconnected = logstream.isDisconnected;
    let numLines = size(log_lines);
    let [configure, setConfigure] = usePersistent('configure', false, 1);
    let [showfilter, setShowfilter] = usePersistent('showfilter', false, 1);
    let [showinfo, setShowinfo] = usePersistent('showinfo', false, 1);
    let [wordwrap, setWordwrap] = usePersistent('wordwrap', false, 1);
    let [formatFull, setFormatFull] = usePersistent('formatFull', false, 1);
    let [caseSensitive, setCaseSensitive] = usePersistent('caseSensitive', false, 1);
    let [channelFilter, setChannelFilter] = usePersistent('channelFilter', '');
    let [msgFilter, setMsgFilter] = usePersistent('msgFilter', '');
    let [activeLevels] = useActiveLevels();
    let [stoppedLogStream, setStoppedLogStream] = useState(null);
    useEffect(()=>{
        setStoppedLogStream(!recordOn ? log_lines : null);
    }, [
        recordOn
    ]);
    log_lines = stoppedLogStream || log_lines;
    let [manualMode, setManualMode] = useState(false);
    useEffect(()=>{
        if (divRef.current) {
            if (!manualMode) {
                divRef.current.scrollTop = 10 ** 6;
            }
        }
    }, [
        log_lines
    ]);
    let setFollow = (v)=>{
        setManualMode(!v);
        if (v && divRef.current) {
            divRef.current.scrollTop = 10 ** 6;
        }
    };
    // XXX: Scroll-Handling broken
    let checkManual = (scrollTop, scrollHeight, height)=>{
        let delta = Math.abs(parseInt(scrollHeight - scrollTop - height));
        setManualMode(delta > 10);
    };
    if (!log_lines) {
        return null;
    }
    //~ let getColor = l => {
    //~ if (l == ) {
    //~ return BLUE_LIGHT
    //~ } else if (l.includes(' INTEREST ')) {
    //~ return GREEN_LIGHT
    //~ } else if (l.includes(' EVENT ')) {
    //~ return CYAN_LIGHT
    //~ } else if (l.includes(' WARNING ')) {
    //~ return YELLOW_LIGHT
    //~ } else if (l.includes(' ERROR ')) {
    //~ return RED_LIGHT
    //~ }
    //~ }
    if (channels) {
        channelFilter = channels.join(',');
    }
    let { filtered_msgs, levelCounts, uniqueChannels } = useMemo(()=>{
        let lines = log_lines;
        if (channelFilter) {
            let line_filter_channels = channelFilter.split(',').map((e)=>channelMatcher(strip(e)));
            lines = applyChannelFilter(lines, line_filter_channels);
        }
        if (msgFilter) {
            lines = applyChannelFilter(lines, new RegExp(msgFilter, caseSensitive ? undefined : 'i'));
        }
        let levelCounts = dict(LEVELS.map((l)=>[
                l,
                0
            ]));
        let msgs = [];
        let last_msg = {};
        let uniqueChannels = new Set();
        lines.forEach((l, i)=>{
            if (l.startsWith(MULTILINE_PREFIX)) {
                last_msg.longtext = strip(l);
            } else {
                let [date, time, channel, level, ...rest] = l.split(' ');
                if (date && channel) {
                    if (!formatFull) {
                        // remove custom channel annotations like oai/1234567/mets/vd
                        channel = channel.split('/')[0];
                    }
                    uniqueChannels.add(channel);
                    levelCounts[level] += 1;
                    let msg = {
                        date,
                        time,
                        channel,
                        level,
                        msg: rest.join(' '),
                        longtext: null,
                        line: l
                    };
                    last_msg = msg;
                    msgs.push(msg);
                }
            }
        });
        return {
            filtered_msgs: msgs,
            levelCounts,
            uniqueChannels: [
                ...uniqueChannels
            ]
        };
    }, [
        log_lines,
        channelFilter,
        msgFilter
    ]);
    let max_channel_s_length = Math.max(...uniqueChannels.map((c)=>c.length));
    let lines = filtered_msgs.filter(({ level })=>activeLevels[level]).map(({ date, time, channel, level, msg, line, longtext }, i)=>{
        let style = {
            margin: 0,
            whiteSpace: wordwrap ? 'pre-line' : undefined
        };
        let color = LEVEL_COLORS[level] || GREY_LIGHT;
        if (!formatFull) {
            let lev_ts = `${level[0]} ${time.slice(0, 8)}`;
            if (channel.startsWith('cherrypy.access')) {
                let s = msg.split(' ').slice(4).join(' ');
                line = `${lev_ts} cp.access ${s}`;
            } else {
                line = `${lev_ts} ${padEnd(channel.startsWith('vls.') ? channel.slice(4) : channel, max_channel_s_length - 7)} ${msg}`;
            }
        }
        return /*#__PURE__*/ _jsxs(React.Fragment, {
            children: [
                /*#__PURE__*/ _jsx("pre", {
                    style: {
                        ...style,
                        color
                    },
                    children: line
                }),
                longtext && /*#__PURE__*/ _jsx("pre", {
                    style: {
                        ...style,
                        marginLeft: '5px',
                        paddingLeft: '5px',
                        borderLeft: `2px solid ${color}`
                    },
                    children: longtext
                })
            ]
        }, i);
    });
    let panel = /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs("div", {
            ref: divRef,
            onScroll: (e)=>checkManual(e.target.scrollTop, e.target.scrollHeight, e.target.clientHeight),
            style: {
                fontSize: '0.8em',
                backgroundColor: 'black',
                color: 'white',
                overflowY: 'auto',
                overflowX: wordwrap ? 'hidden' : 'auto',
                margin: 0,
                flex: 1,
                width: '100%',
                lineHeight: '1.1em'
            },
            children: [
                showinfo && /*#__PURE__*/ _jsxs("span", {
                    style: {
                        right: '10px',
                        position: 'absolute',
                        backgroundColor: '#224',
                        padding: '5px',
                        fontSize: '0.9em'
                    },
                    children: [
                        `${parseInt(now() - start_ts)}ms`,
                        /*#__PURE__*/ _jsx("br", {}),
                        `L${numLines} `,
                        lines.length !== numLines && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx("br", {}),
                                `F${lines.length} `
                            ]
                        }),
                        /*#__PURE__*/ _jsx("br", {}),
                        filtered_msgs[1]?.date,
                        /*#__PURE__*/ _jsx("br", {}),
                        filtered_msgs[1]?.time
                    ]
                }),
                lines
            ]
        })
    });
    let isDialog = logstream.mode == 'dialog';
    let menu = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(XMenu, {
                attached: true,
                children: [
                    /*#__PURE__*/ _jsx(XMenu.Item, {
                        icon: "window close outline",
                        onClick: toggleLogStream,
                        title: "Close"
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Toggle, {
                        icon: "arrow down",
                        set: setFollow,
                        value: !manualMode,
                        title: "Follow/Scroll"
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Item, {
                        icon: "erase",
                        onClick: ()=>setLogStreamData('FLUSH'),
                        title: "Clear"
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Item, {
                        icon: {
                            name: 'refresh',
                            color: isDisconnected ? 'green' : 'grey'
                        },
                        onClick: ()=>toggleLogStream({
                                active: true
                            }),
                        title: "Reconnect logstream"
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Toggle, {
                        icon: "record",
                        set: setRecordOn,
                        value: recordOn,
                        title: "Load new lines"
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Toggle, {
                        icon: "filter",
                        set: setShowfilter,
                        value: showfilter,
                        title: "Show Filter"
                    }),
                    !channels && !logstream.channel && /*#__PURE__*/ _jsx(XMenu.FilterInput, {
                        fluid: true,
                        label: "Chan",
                        debounce: 200,
                        defaultValue: channelFilter,
                        onValueChange: setChannelFilter,
                        title: "filter channel names (separate by comma for multiple)"
                    }),
                    logstream.channel && /*#__PURE__*/ _jsx(ChannelDropdown, {
                        value: logstream.channel,
                        node: node
                    }),
                    /*#__PURE__*/ _jsx(NodeSwitchItem, {
                        node: node,
                        onNodeChange: (v)=>setLogStream({
                                node: v
                            })
                    }),
                    /*#__PURE__*/ _jsx(XMenu.BeginRight, {}),
                    /*#__PURE__*/ _jsx(ChannelInfoItem, {
                        channels: uniqueChannels
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Toggle, {
                        icon: "configure",
                        set: setConfigure,
                        value: configure,
                        title: "More Config"
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Item, {
                        icon: "window maximize outline",
                        active: isDialog,
                        onClick: ()=>setLogStreamMode(isDialog ? 'right' : 'dialog'),
                        title: "Toggle dialog"
                    })
                ]
            }),
            showfilter && /*#__PURE__*/ _jsxs(XMenu, {
                attached: true,
                children: [
                    /*#__PURE__*/ _jsx(XMenu.FilterInput, {
                        fluid: true,
                        label: "Msg",
                        defaultValue: msgFilter,
                        onValueChange: setMsgFilter
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Toggle, {
                        content: "Cc",
                        set: setCaseSensitive,
                        value: caseSensitive,
                        title: "Case sensitve search in Messages"
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Separator, {}),
                    /*#__PURE__*/ _jsx(LevelSelectors, {
                        levelCounts: levelCounts
                    })
                ]
            }),
            configure && /*#__PURE__*/ _jsxs(XMenu, {
                attached: true,
                children: [
                    /*#__PURE__*/ _jsx(XMenu.Item, {
                        icon: "copy",
                        onClick: ()=>navigator.clipboard.writeText(log_lines.join('\n')),
                        title: "Copy to Clipboard"
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Item, {
                        icon: "linewrap",
                        active: wordwrap,
                        onClick: ()=>setWordwrap(!wordwrap),
                        title: "Wrap lines"
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Toggle, {
                        icon: "info",
                        set: setShowinfo,
                        value: showinfo
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Toggle, {
                        icon: "calendar outline",
                        set: setFormatFull,
                        value: formatFull,
                        title: "Verbose Format (show Dates)"
                    }),
                    /*#__PURE__*/ _jsx(XMenu.Input, {
                        label: "Load Lines",
                        maxWidth: 90,
                        debounce: 200,
                        defaultValue: String(logstream.limit),
                        onValueChange: (v)=>setLogStream({
                                limit: parseInt(v) || 50
                            })
                    })
                ]
            })
        ]
    });
    if (as == 'dialog') {
        return /*#__PURE__*/ _jsx(ResizableDialog, {
            name: "logStream",
            closeOnEsc: true,
            onClose: toggleLogStream,
            children: panel
        });
    } else if (as == 'div') {
        return /*#__PURE__*/ _jsxs("div", {
            style: {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            },
            children: [
                menu,
                panel
            ]
        });
    }
};
